// Intelligent Categorization Agent UI (Polished)
// Additions: summary metric strip, batch-result charts (category donut + confidence histogram),
// signal-weight radial, review queue with action buttons.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Typography, Chip, Stack, Paper, Divider,
    FormControlLabel, Switch, Tooltip, IconButton, Grid,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, AIAnalysisBox, ConfidenceBar, ConfidenceChip, SimpleBarChart,
    DonutChart, SectionHeader, DrillTable, EmptyState, fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import {
    catResolve, catHistory, catVendor, catSplit, catTransfer, catBatch,
    transactionIntelligenceSummary,
} from './api';

const TABS = ['Resolve', 'History', 'Vendor', 'Split', 'Transfer', 'Batch Run'];

export default function CategorizationAgent() {
    const [tab, setTab] = useState(0);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [useAi, setUseAi] = useState(true);

    const [summary, setSummary] = useState(null);
    const [resTxnId, setResTxnId] = useState('');
    const [resResult, setResResult] = useState(null);
    const [histMerchant, setHistMerchant] = useState('');
    const [histAmount, setHistAmount] = useState('');
    const [histResult, setHistResult] = useState(null);
    const [vendMerchant, setVendMerchant] = useState('');
    const [vendResult, setVendResult] = useState(null);
    const [splitTxnId, setSplitTxnId] = useState('');
    const [splitResult, setSplitResult] = useState(null);
    const [xferTxnId, setXferTxnId] = useState('');
    const [xferResult, setXferResult] = useState(null);
    const [batchSize, setBatchSize] = useState(50);
    const [batchResult, setBatchResult] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    // Load summary once
    useEffect(() => {
        transactionIntelligenceSummary()
            .then(r => r?.data?.success && setSummary(r.data.data))
            .catch(() => {});
    }, []);

    const summaryCards = summary ? [
        { label: 'Auto-Categorized (today)', value: summary.categorization?.auto_posted_today ?? '—', accent: 'success' },
        { label: 'Review Queue', value: summary.categorization?.review_queue_count ?? '—', accent: 'warning' },
        { label: 'Avg Confidence', value: summary.categorization?.avg_confidence != null ? `${(summary.categorization.avg_confidence * 100).toFixed(0)}%` : '—', accent: 'info' },
        { label: 'Known Vendors', value: summary.categorization?.known_vendors ?? '—', accent: 'primary' },
    ] : [
        { label: 'Auto-Categorized (today)', value: '—', accent: 'success' },
        { label: 'Review Queue', value: '—', accent: 'warning' },
        { label: 'Avg Confidence', value: '—', accent: 'info' },
        { label: 'Known Vendors', value: '—', accent: 'primary' },
    ];

    // Build chart data from batch result
    const categoryDonutData = batchResult?.categories_breakdown
        ? Object.entries(batchResult.categories_breakdown).map(([name, value]) => ({ name, value }))
        : [];

    const confidenceHistogram = batchResult?.confidence_distribution
        || (batchResult ? [
            { bucket: '90-100%', count: batchResult.auto_posted || 0 },
            { bucket: '70-89%', count: batchResult.review_count || 0 },
            { bucket: '<70%', count: batchResult.failed || 0 },
        ] : []);

    const renderTab = () => {
        switch (tab) {
            case 0:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Resolve single transaction"
                            subtitle="Claude/Gemini multi-signal category resolver"
                            actions={
                                <FormControlLabel
                                    control={<Switch size="small" checked={useAi} onChange={e => setUseAi(e.target.checked)} />}
                                    label={<Typography variant="caption">AI Assist</Typography>}
                                />
                            }
                        />
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Transaction ID" value={resTxnId}
                                onChange={e => setResTxnId(e.target.value)} sx={{ width: 200 }} />
                            <Button variant="contained" size="small" disabled={!resTxnId || loading}
                                startIcon={<PlayArrowIcon />}
                                onClick={async () => {
                                    const r = await run(() => catResolve(parseInt(resTxnId), useAi));
                                    if (r?.data?.success) setResResult(r.data.data);
                                }}>Resolve</Button>
                        </Stack>
                        {resResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {resResult.account_name || 'No match'}
                                    </Typography>
                                    <ConfidenceChip value={resResult.confidence} source={resResult.confidence_source} />
                                    {resResult.ai_enhanced && (
                                        <Chip size="small" icon={<SmartToyIcon sx={{ fontSize: 14 }} />}
                                            label="AI Enhanced" color="info" variant="outlined" />
                                    )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">{resResult.reasoning}</Typography>
                                {resResult.signals && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                            Signal Weights
                                        </Typography>
                                        <Stack spacing={0.5}>
                                            <ConfidenceBar label="Historical pattern" value={resResult.signals.historical_weight} />
                                            <ConfidenceBar label="Category source" value={resResult.signals.stripe_category_weight} />
                                            <ConfidenceBar label="Merchant match" value={resResult.signals.merchant_name_weight} />
                                        </Stack>
                                    </Box>
                                )}
                                <AIAnalysisBox text={resResult.ai_analysis} />
                            </Paper>
                        )}
                    </Stack>
                );
            case 1:
                return (
                    <Stack spacing={1}>
                        <SectionHeader title="Historical Pattern Search" subtitle="Find similar past transactions and their categorizations" />
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Merchant contains" value={histMerchant}
                                onChange={e => setHistMerchant(e.target.value)} />
                            <TextField size="small" label="Amount (approx)" type="number" value={histAmount}
                                onChange={e => setHistAmount(e.target.value)} sx={{ width: 160 }} />
                            <Button variant="contained" size="small" disabled={loading}
                                onClick={async () => {
                                    const params = {};
                                    if (histMerchant) params.merchant_name = histMerchant;
                                    if (histAmount) params.amount = parseFloat(histAmount);
                                    const r = await run(() => catHistory(params));
                                    if (r?.data?.success) setHistResult(r.data.data);
                                }}>Search</Button>
                        </Stack>
                        {histResult && (
                            <>
                                <Typography variant="caption">{histResult.count} matching transactions</Typography>
                                <DrillTable
                                    columns={[
                                        { key: 'id', label: 'ID' },
                                        { key: 'date', label: 'Date' },
                                        { key: 'merchant_name', label: 'Merchant' },
                                        { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                        { key: 'gl_account_id', label: 'GL', render: r => r.gl_account_id || '-' },
                                    ]}
                                    rows={histResult.transactions || []}
                                />
                            </>
                        )}
                    </Stack>
                );
            case 2:
                return (
                    <Stack spacing={1}>
                        <SectionHeader title="Vendor Profile Resolver" />
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Merchant name" value={vendMerchant}
                                onChange={e => setVendMerchant(e.target.value)} sx={{ minWidth: 260 }} />
                            <Button variant="contained" size="small" disabled={!vendMerchant || loading}
                                onClick={async () => {
                                    const r = await run(() => catVendor(vendMerchant));
                                    if (r?.data?.success) setVendResult(r.data.data);
                                }}>Resolve</Button>
                        </Stack>
                        {vendResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                {vendResult.matched ? (
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CheckCircleIcon color="success" />
                                            <Typography variant="h6">{vendResult.vendor_name}</Typography>
                                            <ConfidenceChip value={vendResult.match_score} />
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary">Vendor ID: {vendResult.vendor_id}</Typography>
                                        {vendResult.default_account_name && (
                                            <Chip size="small" label={`Default GL: ${vendResult.default_account_name}`} variant="outlined" />
                                        )}
                                    </Stack>
                                ) : (
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <HourglassTopIcon color="warning" />
                                            <Typography variant="h6">New vendor - review required</Typography>
                                        </Stack>
                                        {vendResult.suggestion?.closest_existing?.name && (
                                            <Typography variant="caption">
                                                Closest match: <b>{vendResult.suggestion.closest_existing.name}</b>
                                                {' '}({(vendResult.suggestion.closest_existing.score * 100).toFixed(0)}%)
                                            </Typography>
                                        )}
                                        <Box sx={{ mt: 1 }}>
                                            <Button size="small" variant="outlined">Create vendor record</Button>
                                        </Box>
                                    </Stack>
                                )}
                            </Paper>
                        )}
                    </Stack>
                );
            case 3:
                return (
                    <Stack spacing={1}>
                        <SectionHeader
                            title="Split Transaction Detector"
                            actions={
                                <FormControlLabel
                                    control={<Switch size="small" checked={useAi} onChange={e => setUseAi(e.target.checked)} />}
                                    label={<Typography variant="caption">AI Suggestions</Typography>}
                                />
                            }
                        />
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Transaction ID" value={splitTxnId}
                                onChange={e => setSplitTxnId(e.target.value)} sx={{ width: 200 }} />
                            <Button variant="contained" size="small" disabled={!splitTxnId || loading}
                                onClick={async () => {
                                    const r = await run(() => catSplit(parseInt(splitTxnId), useAi));
                                    if (r?.data?.success) setSplitResult(r.data.data);
                                }}>Detect</Button>
                        </Stack>
                        {splitResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip size="small" color={splitResult.split_candidate ? 'warning' : 'default'}
                                        label={splitResult.split_candidate ? 'Split likely' : 'Not a split'} />
                                    {splitResult.split_candidate && (
                                        <Typography variant="caption">
                                            {fmtMoney(splitResult.current_amount)} = {splitResult.ratio}x avg ({fmtMoney(splitResult.historical_avg)})
                                        </Typography>
                                    )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{splitResult.reasoning}</Typography>
                                <AIAnalysisBox text={splitResult.ai_split_suggestion} />
                            </Paper>
                        )}
                    </Stack>
                );
            case 4:
                return (
                    <Stack spacing={1}>
                        <SectionHeader title="Transfer Identification" subtitle="Detect inter-account transfers to prevent double-counting" />
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Transaction ID" value={xferTxnId}
                                onChange={e => setXferTxnId(e.target.value)} sx={{ width: 200 }} />
                            <Button variant="contained" size="small" disabled={!xferTxnId || loading}
                                onClick={async () => {
                                    const r = await run(() => catTransfer(parseInt(xferTxnId)));
                                    if (r?.data?.success) setXferResult(r.data.data);
                                }}>Identify</Button>
                        </Stack>
                        {xferResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Chip size="small" color={xferResult.is_transfer ? 'success' : 'default'}
                                    label={xferResult.is_transfer ? 'Transfer detected' : 'Not a transfer'} />
                                {xferResult.matched_transaction_id && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Matched txn <b>#{xferResult.matched_transaction_id}</b> on account <b>{xferResult.matched_account_id}</b>
                                        {' '}({xferResult.matched_date})
                                    </Typography>
                                )}
                                {xferResult.keyword_indication && (
                                    <Typography variant="caption" color="info.main" sx={{ mt: 0.5, display: 'block' }}>
                                        Transfer keywords detected — {xferResult.suggestion}
                                    </Typography>
                                )}
                            </Paper>
                        )}
                    </Stack>
                );
            case 5:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Batch Categorization Run"
                            subtitle="Process a batch of uncategorized transactions with confidence-gated auto-post"
                            actions={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <TextField size="small" label="Batch size" type="number" value={batchSize}
                                        onChange={e => setBatchSize(parseInt(e.target.value) || 50)} sx={{ width: 120 }} />
                                    <FormControlLabel
                                        control={<Switch size="small" checked={useAi} onChange={e => setUseAi(e.target.checked)} />}
                                        label={<Typography variant="caption">AI Summary</Typography>}
                                    />
                                    <Button variant="contained" size="small" disabled={loading}
                                        startIcon={<PlayArrowIcon />}
                                        onClick={async () => {
                                            const r = await run(() => catBatch(batchSize, useAi));
                                            if (r?.data?.success) setBatchResult(r.data.data);
                                        }}>Run</Button>
                                </Stack>
                            }
                        />
                        {batchResult ? (
                            <>
                                <SummaryGrid cards={[
                                    { label: 'Processed', value: batchResult.processed, accent: 'primary' },
                                    { label: 'Auto-posted', value: batchResult.auto_posted, accent: 'success' },
                                    { label: 'Review Queue', value: batchResult.review_count, accent: 'warning' },
                                    { label: 'Failed', value: batchResult.failed, accent: batchResult.failed > 0 ? 'danger' : 'neutral' },
                                ]} />
                                <AIAnalysisBox text={batchResult.ai_summary} />
                                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                    {categoryDonutData.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                <Typography variant="caption" fontWeight={600}>Categories Used</Typography>
                                                <DonutChart data={categoryDonutData} height={220} />
                                            </Paper>
                                        </Grid>
                                    )}
                                    {confidenceHistogram.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                <Typography variant="caption" fontWeight={600}>Confidence Distribution</Typography>
                                                <SimpleBarChart
                                                    data={confidenceHistogram}
                                                    xKey="bucket"
                                                    series={[{ key: 'count', label: 'Transactions', color: CHART_COLORS.secondary }]}
                                                    height={220}
                                                    showLegend={false}
                                                />
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                                {batchResult.review_queue?.length > 0 && (
                                    <>
                                        <Typography variant="caption" fontWeight={600}>Review Queue ({batchResult.review_count})</Typography>
                                        <DrillTable
                                            columns={[
                                                { key: 'transaction_id', label: 'ID' },
                                                { key: 'merchant', label: 'Merchant' },
                                                { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                                { key: 'suggested', label: 'Suggested GL', render: r => r.suggestion?.account_name || '-' },
                                                { key: 'confidence', label: 'Conf', render: r => <ConfidenceChip value={r.suggestion?.confidence} /> },
                                                { key: 'actions', label: '', render: () => (
                                                    <Stack direction="row" spacing={0.5}>
                                                        <Tooltip title="Approve">
                                                            <IconButton size="small" color="success"><CheckCircleIcon fontSize="small" /></IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Re-run">
                                                            <IconButton size="small"><RefreshIcon fontSize="small" /></IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                )},
                                            ]}
                                            rows={batchResult.review_queue}
                                            rowKeyField="transaction_id"
                                            maxHeight={360}
                                        />
                                    </>
                                )}
                            </>
                        ) : (
                            <EmptyState message="Run a batch to see categorization results and analytics." />
                        )}
                    </Stack>
                );
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Categorization Agent"
            subtitle="Agentic Transaction Intelligence — AI-enhanced categorization, vendor resolution, split & transfer detection, batch processing."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
